import { Link } from "react-router-dom";
import { Cloud, KeyRound, Laptop } from "lucide-react";
import { AI_PROVIDERS, type AiSettings, type CloudRunStatus } from "@mycompbox/shared";
import { cn } from "@/lib/utils";

export type RunWith = "computer" | "key" | "cloud";

function activeKey(settings: AiSettings | undefined) {
  if (settings?.effectiveKey === "personal") return settings.personalKey;
  if (settings?.effectiveKey === "company") return settings.companyKey;
  return null;
}

/**
 * Whether work of this kind can run on the person's API key right now.
 * Request interviews run on CompBox's server with the key. Planning and
 * building on the key run on a paired computer, where Claude Code uses the
 * key — so they need a computer and an Anthropic key. (Running them in
 * CompBox cloud is its own choice; see `cloudCanRun`.)
 */
export function keyCanRun(
  settings: AiSettings | undefined,
  kind: "interview" | "execution",
  hasComputer = false
): boolean {
  const key = activeKey(settings);
  if (!settings || !key) return false;
  if (kind === "interview") return settings.keyInterviewAvailable;
  return hasComputer && key.provider === "anthropic";
}

/** Whether planning or building can run in CompBox cloud right now. */
export function cloudCanRun(status: CloudRunStatus | undefined): boolean {
  return !!status?.available;
}

/** Cloud hours left this month (never below zero). */
export function cloudHoursLeft(status: Pick<CloudRunStatus, "usedHours" | "includedHours">): number {
  return Math.max(0, status.includedHours - status.usedHours);
}

/** Why a cloud run can't start, in plain words; null when it can. */
export function cloudReasonText(status: CloudRunStatus): string | null {
  switch (status.reason) {
    case null:
      return null;
    case "not_configured":
      return "CompBox cloud isn't set up on this server yet";
    case "no_key":
      return "Needs an Anthropic API key";
    case "key_provider_unsupported":
      return `Needs an Anthropic key; yours is ${status.keyProvider ? AI_PROVIDERS[status.keyProvider].name : "another provider"}`;
    case "repo_not_connected":
      return "This project's repository isn't connected to GitHub";
    case "hours_used_up":
      return `All ${String(status.includedHours)} cloud hours are used this month`;
  }
}

function CloudDetail({ status }: { status: CloudRunStatus | undefined }) {
  if (!status) return <>Checking…</>;
  const reason = cloudReasonText(status);
  if (!reason) {
    return (
      <>
        {Math.round(cloudHoursLeft(status))} of {status.includedHours} cloud hours left this month. Ends with a pull
        request.
      </>
    );
  }
  if (status.reason === "repo_not_connected") {
    return (
      <>
        {reason}.{" "}
        <Link to="/settings#github" className="underline">
          Connect GitHub
        </Link>
      </>
    );
  }
  if (status.reason === "no_key" || status.reason === "key_provider_unsupported") {
    return (
      <>
        {reason}.{" "}
        <Link to="/settings#ai" className="underline">
          Add one in Settings
        </Link>
      </>
    );
  }
  return <>{reason}</>;
}

function Option({
  selected,
  disabled,
  onSelect,
  icon,
  title,
  detail,
}: {
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
  icon: React.ReactNode;
  title: string;
  detail: React.ReactNode;
}) {
  return (
    <div
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={() => {
        if (!disabled) onSelect();
      }}
      onKeyDown={(e) => {
        if (!disabled && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onSelect();
        }
      }}
      className={cn(
        "flex items-start gap-2 rounded-md border p-3 text-sm",
        selected ? "border-primary/60 bg-primary/10" : "border-white/[0.08]",
        disabled ? "opacity-60" : "cursor-pointer hover:border-primary/40"
      )}
    >
      <span className="mt-0.5 shrink-0">{icon}</span>
      <div>
        <p className="font-medium">{title}</p>
        <p className="text-xs text-muted-foreground">{detail}</p>
      </div>
    </div>
  );
}

/**
 * "Run with" picker shown when sending work: the paired computer, the
 * person's API key (their own, or the company key they inherit) or, for
 * planning and building, a temporary CompBox cloud machine.
 */
export function RunWithChoice({
  settings,
  kind,
  hasComputer,
  value,
  onChange,
  offerCloud = false,
  cloudStatus,
}: {
  settings: AiSettings | undefined;
  kind: "interview" | "execution";
  hasComputer: boolean;
  value: RunWith;
  onChange: (value: RunWith) => void;
  /** Also offer "CompBox cloud" (planning and building only). */
  offerCloud?: boolean;
  /** GET /cloud-runs/status for this run; undefined while it loads. */
  cloudStatus?: CloudRunStatus | undefined;
}) {
  const key = activeKey(settings);
  const keyReady = keyCanRun(settings, kind, hasComputer);
  const showCloud = offerCloud && kind === "execution";

  let keyDetail: React.ReactNode;
  if (!key) {
    keyDetail = (
      <>
        No key yet. <Link to="/settings#ai" className="underline">Add one in Settings</Link>
      </>
    );
  } else if (!keyReady) {
    keyDetail =
      kind === "execution" && key.provider !== "anthropic"
        ? "Planning and building need an Anthropic key"
        : kind === "execution"
          ? "Needs a connected computer"
          : "Not available on this server yet";
  } else {
    keyDetail = `${AI_PROVIDERS[key.provider].name} · ${key.model}${settings?.effectiveKey === "company" ? " (company key)" : ""}${kind === "execution" ? ", on your computer" : ""}`;
  }

  return (
    <div className="space-y-1.5">
      <p className="text-sm font-medium leading-none">Run with</p>
      <div className={cn("grid gap-2", showCloud ? "sm:grid-cols-3" : "sm:grid-cols-2")} role="radiogroup" aria-label="Run with">
        <Option
          selected={value === "computer"}
          disabled={!hasComputer}
          onSelect={() => { onChange("computer"); }}
          icon={<Laptop className="h-4 w-4" />}
          title="My computer"
          detail={hasComputer ? "Uses the AI account set up on it" : <Link to="/connect" className="underline">Connect a computer</Link>}
        />
        <Option
          selected={value === "key"}
          disabled={!keyReady}
          onSelect={() => { onChange("key"); }}
          icon={<KeyRound className="h-4 w-4" />}
          title="My API key"
          detail={keyDetail}
        />
        {showCloud ? (
          <Option
            selected={value === "cloud"}
            disabled={!cloudCanRun(cloudStatus)}
            onSelect={() => { onChange("cloud"); }}
            icon={<Cloud className="h-4 w-4" />}
            title="CompBox cloud"
            detail={<CloudDetail status={cloudStatus} />}
          />
        ) : null}
      </div>
    </div>
  );
}
